import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';

// Redux
import { dispatchInfoMessage, dispatchInfoMessageWithoutDuplicates } from 'src/app/actions/core.actions';
import * as BookmarkActions from 'src/app/actions/bookmark.actions';
import * as BookmarkProblemsActions from 'src/app/actions/bookmark-problems.actions';
import * as BookmarkProblemListActions from 'src/app/actions/bookmark-problem-list.actions';
import { createBookmarkProblemList, initializeCreateBookmarkProblemListState } from 'src/app/actions/bookmark-problem-list.actions';
import { RootState } from 'src/app/reducers';
import { getSignedInUser } from 'src/app/selectors/auth.selectors';
import * as BookmarkSelectors from 'src/app/selectors/bookmark.selectors';
import * as BookmarkProblemListSelectors from 'src/app/selectors/bookmark-problem-list.selectors';
import * as BookmarkProblemsSelectors from 'src/app/selectors/bookmark-problems.selectors';
import { getCreateBookmarkProblemListResult } from 'src/app/selectors/bookmark-problem-list.selectors';

import { PopoverComponent } from '../popover/popover.component';
import { Log } from 'src/app/utils/log';

// models
import { AddBookmarkData, DeleteBookmarkData } from 'src/app/models/bookmark';
import { ToggleProblemToFromBookmarkProblemListRequest } from 'src/app/models/add-problem-to-bookmark-problem-list';
import { BookmarkProblemsCollection } from 'src/app/models/bookmark-problem';
import { BookmarkProblemList } from 'src/app/models/bookmark-problem-list';
import { User } from 'src/app/models/user';
import { Dates } from 'src/app/utils/dates';
import { CreateBookmarkProblemListRequest, VisibilityType } from 'src/app/models/create-bookmark-problem-list';
import { BOOKMARK_PROBLEM_LIST_NAME_MAX_COUNT, UNCATEGORIZED_BOOKMARK_PROBLEM_LISTS_DEFAULT_MAX_COUNT } from 'src/app/resources/config';
import { BookmarkProblemListsUtil } from '../../../utils/bookmark-problem-lists-util';

export interface BookmarkProblemListWithCheck {
  listId: string;
  name: string;
  createdAt: string;
  isProblemCountMaxExceeded: boolean;
  checked: boolean;
}

export interface UpdateBookmarkProblemList {
  problemId: string;
  newBookmarkLists: BookmarkProblemListWithCheck[];
  newIsBookmarked: boolean;
}

@Component({
  selector: 'app-button-toggle-bookmark-with-popover',
  templateUrl: './button-toggle-bookmark-with-popover.component.html',
  styleUrls: ['./button-toggle-bookmark-with-popover.component.scss']
})
export class ButtonToggleBookmarkWithPopoverComponent implements OnInit {
  private LOG_SOURCE = this.constructor.name;

  @Input() isBookmarked: boolean;
  @Input() disabled = false;
  @Input() canSpinner = false;
  @Input() showSpinner = false;
  @Input() problemId: string;
  @Input() subjectId: string;
  @Input() bookmarkProblemLists: BookmarkProblemListWithCheck[];
  @Input() problemMaxCount: number;
  @Input() uncategorizedBookmarkProblemCount: number;
  @Input() bookmarkProblemListId: string;

  @Output() updateBookmarkProblemListPerProblem = new EventEmitter<UpdateBookmarkProblemList>();
  @Output() updateMaxCountFlag = new EventEmitter<UpdateBookmarkProblemList>();
  @Output() updateNewBookmarkProblemLists = new EventEmitter<{
    newBookmarkLists: BookmarkProblemListWithCheck[];
    isBookmarked: boolean;
    newListIds: string[];
    problemId: string;
  }>();

  @ViewChild('bookmarkPopover') bookmarkPopover: PopoverComponent;

  signedInUser$: Observable<User>;
  isFirstTimeAddingToUncategorizedList = false;
  isLimitExceeded = false;
  uncategorizedListUpdateMessage = '未分類のリストに登録しました';
  isUpdateProblemMaxCount: boolean;
  isUpdateNewLists: boolean;
  newListIds: string[] = [];
  listName = '';

  protected readonly BOOKMARK_PROBLEM_LIST_NAME_MAX_COUNT = BOOKMARK_PROBLEM_LIST_NAME_MAX_COUNT;

  constructor(private store: Store<RootState>, private router: Router) {}

  ngOnInit() {
    this.setUpUser();
    this.setUpIsLimitExceeded();
  }

  /**
   * お気に入り登録していない状態でクリックした場合の処理
   */
  handleOffClick() {
    console.log('handleOFFClick this.bookmarkProblemLists: ', this.bookmarkProblemLists);

    if (this.disabled) {
      return;
    }

    // 未分類のリストの問題数が上限に達していない場合は、既存のお気に入りリストの登録APIを実行
    const uncategorizedProblemList = this.bookmarkProblemLists.find(list => list.listId === `uncategorized_${this.subjectId}`);

    if (uncategorizedProblemList) {
      if (uncategorizedProblemList.isProblemCountMaxExceeded) {
        // 既に問題数が上限に達している場合は何も処理を行わない
      } else {
        // 未分類のリストを登録状態に変更(☆を赤に変更)
        this.bookmarkProblemLists = this.bookmarkProblemLists.map(list => ({
          ...list,
          checked: list.checked || list.listId === `uncategorized_${this.subjectId}`
        }));
        // 未分類のリストに問題を追加 - モダルを開いた時に自動で登録するのは、問題のお気に入り登録がされていない時のみ
        if (!this.isBookmarked) {
          this.isFirstTimeAddingToUncategorizedList = true;
          this.addUncategorizedBookmark();
        }
        // お気に入り登録状態に変更(☆を赤に変更)
        this.isBookmarked = true;
      }
    }
  }

  /**
   * お気に入り登録した状態でクリックした場合の処理
   */
  handleOnClick() {
    console.log('handleOnClick this.bookmarkProblemLists: ', this.bookmarkProblemLists);
  }

  /**
   * チェックボックスをクリックした場合の処理
   */
  onListChange(listId: string, checked: boolean, isCountMaxExceeded: boolean) {
    console.log(`リスト ${listId} が ${checked ? '選択' : '解除'} されました`);

    // bookmarkProblemLitsの該当のリストのcheckedを更新する
    this.bookmarkProblemLists = this.bookmarkProblemLists.map(list => ({
      ...list,
      checked: list.listId === listId ? checked : list.checked
    }));

    if (checked) {
      if (listId === `uncategorized_${this.subjectId}`) {
        // 未分類のリストの問題数が上限に達していない場合は、既存のお気に入りリストの登録APIを実行
        const uncategorizedProblemList = this.bookmarkProblemLists.find(list => list.listId === `uncategorized_${this.subjectId}`);
        if (uncategorizedProblemList && !uncategorizedProblemList.isProblemCountMaxExceeded) {
          const isAnyListsChecked = this.bookmarkProblemLists.some(list => list.checked);
          this.isFirstTimeAddingToUncategorizedList = !isAnyListsChecked;
          this.addUncategorizedBookmark();
        }
      } else {
        this.addProblemToList(listId, this.subjectId);
      }
    } else {
      if (listId === `uncategorized_${this.subjectId}`) {
        this.deleteUncategorizedBookmark(isCountMaxExceeded);
      } else {
        this.deleteProblemFromList(listId, isCountMaxExceeded);
      }
    }

    // 全てのチェックが外された場合は☆のアイコンを白にする
    this.isBookmarked = !!this.bookmarkProblemLists.find(list => list.checked);
  }

  createBookmarkProblemList() {
    Log.debug(this.LOG_SOURCE, 'お気に入りリストを作成します');
    this.store.dispatch(initializeCreateBookmarkProblemListState());
    const newListName = this.listName.trim();

    this.listName = '';

    combineLatest([this.signedInUser$])
      .pipe(take(1))
      .subscribe(([user]) => {
        // Reduxステートの操作 BookmarkProblemListsに新しいリストを追加
        const now = Dates.now();

        const tempId = `temp-${newListName}`;
        const bookmarkProblemList: BookmarkProblemList = {
          id: tempId,
          name: newListName,
          userId: user.id,
          schoolId: user.schoolId,
          subjectId: this.subjectId,
          visibility: VisibilityType.PRIVATE,
          problemCount: 0,
          folderId: null,
          createdAt: now,
          updatedAt: now
        };
        this.store.dispatch(BookmarkProblemListActions.addBookmarkProblemList({ bookmarkProblemList }));

        // モダル内のリストの更新
        const newListObj: BookmarkProblemListWithCheck = {
          listId: tempId,
          name: newListName,
          createdAt: now,
          isProblemCountMaxExceeded: true, // 実際のIDが発行されるまで、新しいリストはdisabled
          checked: false
        };
        this.bookmarkProblemLists.splice(1, 0, newListObj); // 未分類のリストの後に追加

        // 追加APIの呼び出し
        const request: CreateBookmarkProblemListRequest = {
          subjectId: this.subjectId,
          name: newListName,
          visibility: VisibilityType.PRIVATE
        };

        this.store.dispatch(createBookmarkProblemList(request));
        this.store
          .select(getCreateBookmarkProblemListResult)
          .pipe(
            filter(it => it != null),
            take(1)
          )
          .subscribe(result => {
            Log.debug(this.LOG_SOURCE, `お気に入りリストの作成結果: `, result);
            if (result.success) {
              const realId = result.response.id;
              // モダル内の新規リストのidを置き換え、使用可能にし、他の問題にも新規リストを更新する
              this.bookmarkProblemLists = this.bookmarkProblemLists.map(list =>
                list.listId === tempId ? { ...list, listId: realId, isProblemCountMaxExceeded: false } : list
              );
              this.isUpdateNewLists = true;
              this.newListIds.push(realId);

              // 上限超過フラグを設定する
              this.setUpIsLimitExceeded();

              this.store.dispatch(
                dispatchInfoMessage({
                  message: '新しいリストを作成して追加しました'
                })
              );

              return;
            }

            // *** APIでエラーが発生した場合の処理 *** //
            // bookmarkProblemListsステートに追加したリストを削除 (Redux Stateの状態を更新する)
            this.store.dispatch(BookmarkProblemListActions.deleteBookmarkProblemListOnState({ tempId }));
            // モダル内の新規リストを削除する
            this.bookmarkProblemLists = this.bookmarkProblemLists.filter(list => list.listId !== tempId);

            Log.warn(this.LOG_SOURCE, `お気に入りリストの作成エラー: err.code: ${result.error ? result.error.code : 'none'}`, result.error);
            this.store.dispatch(
              dispatchInfoMessage({
                message: result.error ? `[${result.error.code}] ${result.error.message}` : 'お気に入りリストに作成中にエラーが発生しました'
              })
            );
          });
      });
  }

  navigateToBookmarkManagement() {
    this.bookmarkPopover.close();
    this.router.navigate(['/bookmark-problem-lists']);
  }

  processAfterDialogClose() {
    if (this.isUpdateProblemMaxCount) {
      // 問題数上限超過フラグの更新処理と各問題のお気に入りリストの状態の更新処理
      this.updateMaxCountFlag.emit({
        newBookmarkLists: this.bookmarkProblemLists,
        newIsBookmarked: this.isBookmarked,
        problemId: this.problemId
      });
      this.isUpdateProblemMaxCount = false;
    } else {
      // 各問題のお気に入りリストの状態を更新
      this.updateBookmarkProblemListPerProblem.emit({
        problemId: this.problemId,
        newBookmarkLists: this.bookmarkProblemLists,
        newIsBookmarked: this.isBookmarked
      });
    }

    if (this.isUpdateNewLists) {
      // 新規作成したリストを他の問題にも更新
      this.updateNewBookmarkProblemLists.emit({
        newBookmarkLists: this.bookmarkProblemLists,
        isBookmarked: this.isBookmarked,
        newListIds: this.newListIds,
        problemId: this.problemId
      });
      this.isUpdateNewLists = false;
      this.newListIds = [];
    }
  }

  private setUpUser() {
    this.signedInUser$ = this.store.select(getSignedInUser).pipe(filter<User>(it => it != null));
  }

  private setUpIsLimitExceeded() {
    this.signedInUser$.pipe(take(1)).subscribe(signedInUser => {
      const listMaxCount = BookmarkProblemListsUtil.getListsMaxCount(signedInUser.schoolId);
      this.isLimitExceeded =
        this.bookmarkProblemLists.filter(bookmarkProblemList => !bookmarkProblemList.listId.includes('uncategorized')).length >=
        listMaxCount
          ? true
          : false;
    });
  }

  private addUncategorizedBookmark() {
    // Reduxステート bookmarkProblemLists のproblemCountの値にプラス１をする
    this.store.dispatch(
      BookmarkProblemListActions.addProblemCount({
        listId: `uncategorized_${this.subjectId}`
      })
    );

    // 登録上限のチェック - 問題数の上限に達した場合は、該当のフラグをオンにする
    combineLatest([
      this.store.select(BookmarkProblemListSelectors.getBookmarkProblemLists).pipe(
        filter(it => it != null),
        take(1)
      ),
      this.signedInUser$
    ])
      .pipe(take(1))
      .subscribe(([bookmarkLists, user]) => {
        const problemCount = bookmarkLists.find(bookmarkList => bookmarkList.id === `uncategorized_${this.subjectId}`).problemCount;

        if (problemCount >= UNCATEGORIZED_BOOKMARK_PROBLEM_LISTS_DEFAULT_MAX_COUNT) {
          this.isUpdateProblemMaxCount = true;
          this.bookmarkProblemLists = this.bookmarkProblemLists.map(list => ({
            ...list,
            isProblemCountMaxExceeded: list.listId === `uncategorized_${this.subjectId}` ? true : list.isProblemCountMaxExceeded
          }));
        }

        // 既存のお気に入り登録APIの実行
        Log.debug(this.LOG_SOURCE, '未分類のお気に入りを登録します');

        const request: AddBookmarkData = {
          userId: user.id,
          subjectId: this.subjectId,
          problemId: this.problemId
        };

        this.store.dispatch(BookmarkActions.addBookmark(request));
        this.store
          .select(BookmarkSelectors.getAddBookmarkResult)
          .pipe(
            filter(it => it != null),
            take(1)
          )
          .subscribe(result => {
            Log.debug(this.LOG_SOURCE, `未分類のリストに問題を追加: `, result);

            if (result.success) {
              this.store.dispatch(
                dispatchInfoMessageWithoutDuplicates({
                  message: this.isFirstTimeAddingToUncategorizedList ? this.uncategorizedListUpdateMessage : '更新しました'
                })
              );
            } else {
              Log.warn(
                this.LOG_SOURCE,
                `未分類のリストに問題を追加する際にエラーが発生: エラーコード: ${result.error ? result.error.code : 'none'}`,
                result.error
              );
              this.store.dispatch(
                dispatchInfoMessageWithoutDuplicates({
                  message: result.error ? `[${result.error.code}] ${result.error.message}` : '未分類のリストに登録中にエラーが発生しました'
                })
              );
              // お気に入りリスト一覧の該当リストの問題数から1を引く
              this.store.dispatch(BookmarkProblemListActions.subtractProblemCount({ listId: `uncategorized_${this.subjectId}` }));

              // 問題数が上限だった場合は、上限超過のフラグをオフに戻す
              if (this.isUpdateProblemMaxCount) {
                this.bookmarkProblemLists = this.bookmarkProblemLists.map(list => ({
                  ...list,
                  isProblemCountMaxExceeded: list.listId === `uncategorized_${this.subjectId}` ? false : list.isProblemCountMaxExceeded
                }));
              }
            }

            this.isFirstTimeAddingToUncategorizedList = false;
            this.store.dispatch(BookmarkActions.initializeAddBookmarkState());
          });
      });
  }

  private deleteUncategorizedBookmark(isCountMaxExceeded: boolean) {
    // Reduxステート bookmarkProblemLists のproblemCountの値にマイナス１をする
    this.store.dispatch(
      BookmarkProblemListActions.subtractProblemCount({
        listId: `uncategorized_${this.subjectId}`
      })
    );

    // 登録上限のチェック - 問題数の上限から削除した場合は、登録上限のフラグをオフにする
    combineLatest([
      this.store.select(BookmarkProblemListSelectors.getBookmarkProblemLists).pipe(
        filter(it => it != null),
        take(1)
      ),
      this.signedInUser$
    ])
      .pipe(take(1))
      .subscribe(([bookmarkLists, user]) => {
        const problemCount = bookmarkLists.find(bookmarkList => bookmarkList.id === `uncategorized_${this.subjectId}`).problemCount;

        if (isCountMaxExceeded) {
          if (problemCount < UNCATEGORIZED_BOOKMARK_PROBLEM_LISTS_DEFAULT_MAX_COUNT) {
            this.isUpdateProblemMaxCount = true;
            this.bookmarkProblemLists = this.bookmarkProblemLists.map(list => ({
              ...list,
              isProblemCountMaxExceeded: list.listId === `uncategorized_${this.subjectId}` ? false : list.isProblemCountMaxExceeded
            }));
          }
        }

        Log.debug(this.LOG_SOURCE, '未分類のお気に入りを解除します');
        const request: DeleteBookmarkData = {
          userId: user.id,
          subjectId: this.subjectId,
          problemId: this.problemId
        };

        this.store.dispatch(BookmarkActions.deleteBookmark(request));

        this.store
          .select(BookmarkSelectors.getDeleteBookmarkResult)
          .pipe(
            filter(it => it != null),
            take(1)
          )
          .subscribe(result => {
            if (result.success) {
              this.store.dispatch(
                dispatchInfoMessageWithoutDuplicates({
                  message: `更新しました`
                })
              );
            } else {
              Log.warn(this.LOG_SOURCE, `delete bookmark error: err.code: ${result.error ? result.error.code : 'none'}`, result.error);
              this.store.dispatch(
                dispatchInfoMessageWithoutDuplicates({
                  message: result.error
                    ? `[${result.error.code}] ${result.error.message}`
                    : '未分類のお気に入りリストを解除中にエラーが発生しました'
                })
              );

              // Reduxステート bookmarkProblemLists のproblemCountの値にプラス１をする (Revert処理)
              this.store.dispatch(
                BookmarkProblemListActions.addProblemCount({
                  listId: `uncategorized_${this.subjectId}`
                })
              );
              // 問題数が上限に達していたリストの場合は、上限超過のフラグをオンに戻す(Revert処理)
              if (this.isUpdateProblemMaxCount) {
                this.bookmarkProblemLists = this.bookmarkProblemLists.map(list => ({
                  ...list,
                  isProblemCountMaxExceeded: list.listId === `uncategorized_${this.subjectId}` ? true : list.isProblemCountMaxExceeded
                }));
              }
            }

            this.store.dispatch(BookmarkActions.initializeDeleteBookmarkState());
          });
      });
  }

  private addProblemToList(listId: string, subjectId: string) {
    // bookmarProblemListsステートのProblemCountの値を変更
    this.store.dispatch(BookmarkProblemListActions.addProblemCount({ listId }));

    combineLatest([
      this.store.select(BookmarkProblemListSelectors.getBookmarkProblemLists).pipe(
        filter(it => it != null),
        take(1)
      ),
      this.signedInUser$
    ])
      .pipe(take(1))
      .subscribe(([bookmarkLists, user]) => {
        const problemCount = bookmarkLists.find(bookmarkList => bookmarkList.id === listId).problemCount;
        // 問題数の上限に達した場合は、該当のフラグをオンにする
        if (problemCount >= this.problemMaxCount) {
          this.isUpdateProblemMaxCount = true;
          this.bookmarkProblemLists = this.bookmarkProblemLists.map(list => ({
            ...list,
            isProblemCountMaxExceeded: list.listId === listId ? true : list.isProblemCountMaxExceeded
          }));
        }

        // bookmarkProblemsステートに問題を追加 - idは仮で発行
        const tempId = `temp_${listId}_${this.problemId}`;
        const now = Dates.now();
        const collectionData: BookmarkProblemsCollection = {
          id: tempId,
          bookmarkProblemListId: listId,
          userId: user.id,
          subjectId,
          problemId: this.problemId,
          order: 0,
          createdAt: now,
          updatedAt: now
        };
        this.store.dispatch(BookmarkProblemsActions.addProblem({ tempId, collectionData }));

        // お気に入りリストへの問題登録APIを呼び出す
        this.store.dispatch(BookmarkProblemListActions.initializeAddProblemToBookmarkProblemListState());
        const request: ToggleProblemToFromBookmarkProblemListRequest = {
          problemId: this.problemId,
          listId,
          checked: true
        };
        this.store.dispatch(BookmarkProblemListActions.toggleProblemToFromBookmarkProblemList(request));
        this.store
          .select(BookmarkProblemListSelectors.getAddProblemToBookmarkProblemListResult)
          .pipe(
            filter(it => it != null),
            take(1)
          )
          .subscribe(result => {
            Log.debug(this.LOG_SOURCE, `お気に入りリストに問題を追加: `, result);

            if (result.success) {
              // bookmarkProblemsステートのidを実際のidに上書き (Redux Stateの状態を更新する)
              const realId = result.response.bookmarkProblemId;
              this.store.dispatch(BookmarkProblemsActions.updateBookmarkProblemsId({ tempId, realId }));
            } else {
              Log.warn(
                this.LOG_SOURCE,
                `お気に入りリストに問題を追加する際にエラーが発生: エラーコード: ${result.error ? result.error.code : 'none'}`,
                result.error
              );
              // お気に入りリスト一覧の該当リストの問題数から1を引く (Redux Stateの状態を更新する)
              this.store.dispatch(BookmarkProblemListActions.subtractProblemCount({ listId }));
              // 問題数が上限だった場合は、上限超過のフラグをオフに戻す
              if (this.isUpdateProblemMaxCount) {
                this.bookmarkProblemLists = this.bookmarkProblemLists.map(list => ({
                  ...list,
                  isProblemCountMaxExceeded: list.listId === listId || list.isProblemCountMaxExceeded
                }));
              }
              // bookmarkProblemsステートから問題を削除
              this.store.dispatch(BookmarkProblemsActions.deleteProblem({ listId, problemId: this.problemId }));
            }

            this.store.dispatch(BookmarkProblemListActions.dispatchToggleProblemResultMessage());
            this.store.dispatch(BookmarkProblemListActions.initializeAddProblemToBookmarkProblemListState());
          });
      });
  }

  private deleteProblemFromList(listId: string, isCountMaxExceeded: boolean) {
    // bookmarProblemListsステートのProblemCountの値を変更
    this.store.dispatch(BookmarkProblemListActions.subtractProblemCount({ listId }));

    // 問題数が上限に達しているリスト場合は、削除の際に、上限超過のフラグをオフにする
    combineLatest([
      this.store.select(BookmarkProblemListSelectors.getBookmarkProblemLists).pipe(
        filter(it => it != null),
        take(1)
      )
    ]).subscribe(([bookmarkLists]) => {
      //this.isUpdateProblemMaxCount = false;
      if (isCountMaxExceeded) {
        const problemCount = bookmarkLists.find(bookmarkList => bookmarkList.id === listId).problemCount;
        if (problemCount < this.problemMaxCount) {
          this.isUpdateProblemMaxCount = true;
          this.bookmarkProblemLists = this.bookmarkProblemLists.map(list => ({
            ...list,
            isProblemCountMaxExceeded: list.listId === listId ? false : list.isProblemCountMaxExceeded
          }));
        }
      }

      // bookmarkProblemsステートから問題を削除
      this.store.dispatch(BookmarkProblemsActions.deleteProblem({ listId, problemId: this.problemId }));

      // リストから問題を削除するAPIを呼び出す
      this.store.dispatch(BookmarkProblemListActions.initializeDeleteProblemFromBookmarkProblemListState());

      const request: ToggleProblemToFromBookmarkProblemListRequest = {
        problemId: this.problemId,
        listId,
        checked: false,
        isListIdMatched: this.bookmarkProblemListId === listId ? true : false // 追加: 現在のリストIDと一致するかどうか
      };
      this.store.dispatch(BookmarkProblemListActions.toggleProblemToFromBookmarkProblemList(request));
      this.store
        .select(BookmarkProblemListSelectors.getDeleteProblemFromBookmarkProblemListResult)
        .pipe(
          filter(it => it != null),
          take(1)
        )
        .subscribe(result => {
          Log.debug(this.LOG_SOURCE, `お気に入りリストから問題を削除: `, result);
          if (!result.success) {
            Log.warn(
              this.LOG_SOURCE,
              `お気に入りリストから問題を削除する際にエラーが発生: エラーコード: ${result.error ? result.error.code : 'none'}`,
              result.error
            );
            // bookmarProblemListsステートのProblemCountの値を変更
            this.store.dispatch(BookmarkProblemListActions.addProblemCount({ listId }));
            // 問題数が上限に達していたリストの場合は、上限超過のフラグをオンに戻す
            if (this.isUpdateProblemMaxCount) {
              this.bookmarkProblemLists = this.bookmarkProblemLists.map(list => ({
                ...list,
                isProblemCountMaxExceeded: list.listId === listId || list.isProblemCountMaxExceeded
              }));
            }

            // bookmarkProblemsステートに問題を追加（Revert処理）
            this.store
              .select(BookmarkProblemsSelectors.getBookmarkProblemToDelete)
              .pipe(
                filter(it => it != null),
                take(1)
              )
              .subscribe(bookmarkProblem => {
                this.store.dispatch(BookmarkProblemsActions.addProblem({ tempId: bookmarkProblem.id, collectionData: bookmarkProblem }));
              });
          }

          this.store.dispatch(BookmarkProblemListActions.dispatchToggleProblemResultMessage());
          this.store.dispatch(BookmarkProblemListActions.initializeDeleteProblemFromBookmarkProblemListState());
        });
    });
  }
}
